import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import {
  ChangePasswordRequest,
  EditUserRequest,
  LoginRequest,
  UserPublic,
  Verify2faRequest,
} from '@front/shared/data-access-cms';
import { GENERAL_ERROR_MESSAGE } from '@front/shared/util-i18n';
import { SnackbarService } from '@front/shared/util-notifications/snackbar';
import { StorageService } from '@irontec/ngx-encrypted-storage';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, filter, forkJoin, from, map, of, pipe, switchMap, tap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { Status } from './../models/auth.models';

const storeName = 'auth';

export interface AuthStateSignal {
  status: Status;
  refreshing: boolean;
  jwt: string | null;
  refreshToken: string | null;
  QR2fa: string | null;
  user: UserPublic | null;
  remember: boolean;
}

const initialState: AuthStateSignal = {
  status: null,
  refreshing: false,
  jwt: null,
  refreshToken: null,
  QR2fa: null,
  user: null,
  remember: true,
};

const NAVIGATE_TO_PRIVATE_AREA = ['/', 'private-area', 'mi-zona'];
const NAVIGATE_TO_LOGIN = ['/', 'auth', 'login'];
const NAVIGATE_TO_2FA = ['/', 'auth', '2fa'];
const SIGNAL_USER_EMAIL_KEY = 'USER_EMAIL';
const SIGNAL_JWT_KEY = 'JWT';
const SIGNAL_REFRESH_KEY = 'REFRESH';

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      authService = inject(AuthService),
      router = inject(Router),
      storage = inject(StorageService),
      snackBar = inject(SnackbarService)
    ) => ({
      init: rxMethod<void>(
        pipe(
          filter(() => isPlatformBrowser(inject(PLATFORM_ID))),
          tap(() => patchState(store, { status: 'loading' })),
          switchMap(() =>
            forkJoin({
              jwt: from(storage.getItem<string>(SIGNAL_JWT_KEY, storeName)).pipe(
                catchError((error) => {
                  console.warn('Error reading JWT from storage, clearing:', error);
                  storage.removeItem(SIGNAL_JWT_KEY, storeName);
                  return of(null);
                })
              ),
              refreshToken: from(storage.getItem<string>(SIGNAL_REFRESH_KEY, storeName)).pipe(
                catchError((error) => {
                  console.warn('Error reading refresh token from storage, clearing:', error);
                  storage.removeItem(SIGNAL_REFRESH_KEY, storeName);
                  return of(null);
                })
              ),
            }).pipe(
              catchError((error) => {
                console.error('Critical error initializing auth, clearing all auth storage:', error);
                // Limpieza completa si todo falla
                storage.removeItem(SIGNAL_JWT_KEY, storeName);
                storage.removeItem(SIGNAL_REFRESH_KEY, storeName);
                storage.removeItem(SIGNAL_USER_EMAIL_KEY, storeName);
                patchState(store, { status: 'idle' });
                return EMPTY;
              }),
              switchMap(({ jwt, refreshToken }) => {
                if (jwt) {
                  patchState(store, {
                    jwt: jwt || null,
                    refreshToken: refreshToken || null,
                  });
                  return authService.getUserInfo().pipe(
                    tapResponse({
                      next: (user) => {
                        patchState(store, {
                          status: 'success',
                          user,
                        });
                      },
                      error: (error) => {
                        console.error('Error getting user info, clearing auth:', error);
                        // Si el token está corrupto o inválido, limpia todo
                        storage.removeItem(SIGNAL_JWT_KEY, storeName);
                        storage.removeItem(SIGNAL_REFRESH_KEY, storeName);
                        patchState(store, {
                          status: 'error',
                          jwt: null,
                          refreshToken: null,
                          user: null
                        });
                      },
                    })
                  );
                } else {
                  patchState(store, { status: 'idle' });
                  return EMPTY;
                }
              })
            )
          )
        )
      ),
      login: rxMethod<{ loginRequest: LoginRequest; remember: boolean }>(
        pipe(
          tap(() => patchState(store, { ...initialState, status: 'loading' })),
          tap(({ loginRequest, remember }) => {
            if (remember) {
              storage.setItem(
                SIGNAL_USER_EMAIL_KEY,
                loginRequest.identifier,
                storeName
              );
            } else {
              storage.removeItem(SIGNAL_USER_EMAIL_KEY, storeName);
            }
          }),
          switchMap((credientials) =>
            forkJoin([
              storage.removeItem(SIGNAL_JWT_KEY, storeName),
              storage.removeItem(SIGNAL_REFRESH_KEY, storeName),
            ]).pipe(map(() => credientials))
          ),
          switchMap(({ loginRequest, remember }) =>
            authService.login(loginRequest).pipe(
              tapResponse({
                next: (response) => {
                  if (response && 'code' in response) {
                    patchState(store, {
                      status: 'success',
                    });
                    router.navigate(NAVIGATE_TO_2FA, {
                      state: {
                        remember,
                        code: response.code,
                        hasSecret: response.hasSecret,
                      },
                    });
                  } else if (response && 'jwt' in response) {
                    patchState(store, {
                      status: 'success',
                      jwt: response.jwt,
                      refreshToken: response.refreshToken,
                      user: response.user,
                    });
                    if (remember) {
                      storage.setItem(
                        SIGNAL_REFRESH_KEY,
                        response?.refreshToken,
                        storeName
                      );
                      storage.setItem(SIGNAL_JWT_KEY, response?.jwt, storeName);
                    }
                    router.navigate(NAVIGATE_TO_PRIVATE_AREA);
                  } else {
                    patchState(store, {
                      status: 'error',
                    });
                  }
                },
                error: (e: unknown) => {
                  patchState(store, { status: 'error' });
                  if (e instanceof HttpErrorResponse && e.status === 401) {
                    const message = $localize`Credenciales incorrectas`;
                    snackBar.displayMessage(message);
                  } else {
                    snackBar.displayMessage(GENERAL_ERROR_MESSAGE);
                  }
                },
              })
            )
          )
        )
      ),
      refresh: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { refreshing: true })),
          switchMap(() =>
            authService.performRefresh(store.refreshToken()).pipe(
              tapResponse({
                next: (response) => {
                  storage.setItem(
                    SIGNAL_REFRESH_KEY,
                    response?.refreshToken,
                    storeName
                  );
                  storage
                    .setItem(SIGNAL_JWT_KEY, response?.jwt, storeName)
                    .then(() =>
                      patchState(store, {
                        refreshing: false,
                        jwt: response?.jwt,
                        refreshToken: response?.refreshToken,
                        user: response?.user,
                      })
                    );
                },
                error: () => {
                  storage.removeItem(SIGNAL_JWT_KEY, storeName);
                  storage.removeItem(SIGNAL_REFRESH_KEY, storeName);
                  patchState(store, {
                    status: 'error',
                    refreshing: false,
                    jwt: null,
                    refreshToken: null,
                    user: null,
                  });
                },
              })
            )
          )
        )
      ),
      logout(): void {
        storage.removeItem(SIGNAL_JWT_KEY, storeName);
        storage.removeItem(SIGNAL_REFRESH_KEY, storeName);
        patchState(store, initialState);
        router.navigate(NAVIGATE_TO_LOGIN);
      },
      editUser: rxMethod<EditUserRequest>(
        pipe(
          tap(() => patchState(store, { status: 'loading' })),
          switchMap((editRequest) =>
            authService.editUser(store.user()?.id || '', editRequest).pipe(
              tapResponse({
                next: (user) => {
                  patchState(store, { status: 'success', user });
                  const message = $localize`Usuario actualizado correctamente`;
                  snackBar.displayMessage(message);
                },
                error: () => {
                  patchState(store, { status: 'error' });
                },
              })
            )
          )
        )
      ),
      changePassword: rxMethod<ChangePasswordRequest>(
        pipe(
          tap(() => patchState(store, { status: 'loading' })),
          switchMap((changePasswordRequest) =>
            authService.changePassword(changePasswordRequest).pipe(
              tapResponse({
                next: () => {
                  patchState(store, { status: 'success' });
                  const message = $localize`Contraseña actualizada correctamente`;
                  snackBar.displayMessage(message);
                },
                error: () => {
                  patchState(store, { status: 'error' });
                },
              })
            )
          )
        )
      ),
      generate2faSecret: rxMethod<{ code: string; OTPName: string }>(
        pipe(
          tap(() => patchState(store, { status: 'loading' })),
          switchMap((secretData) =>
            authService.generateSecret2fa(secretData).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    status: 'success',
                    QR2fa: response?.QRCodeData,
                  });
                },
                error: () => {
                  patchState(store, { status: 'error' });
                },
              })
            )
          )
        )
      ),
      verify2fa: rxMethod<Verify2faRequest>(
        pipe(
          tap(() => patchState(store, { status: 'loading' })),
          switchMap((verify2faRequest) =>
            authService.verify2fa(verify2faRequest).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    status: 'idle',
                    jwt: response?.jwt,
                    refreshToken: response?.refreshToken,
                    user: response?.user,
                  });

                  const remember = store.remember();
                  const user = store.user();
                  const jwt = store.jwt();
                  const refreshToken = store.refreshToken();

                  if (remember && user) {
                    storage.setItem(
                      SIGNAL_USER_EMAIL_KEY,
                      user.email,
                      storeName
                    );
                  }

                  if (remember) {
                    storage.setItem(SIGNAL_JWT_KEY, jwt, storeName);
                    storage.setItem(
                      SIGNAL_REFRESH_KEY,
                      refreshToken,
                      storeName
                    );
                  } else {
                    storage.removeItem(SIGNAL_JWT_KEY, storeName);
                    storage.removeItem(SIGNAL_REFRESH_KEY, storeName);
                  }

                  router.navigate(NAVIGATE_TO_PRIVATE_AREA);
                },
                error: () => {
                  patchState(store, { status: 'error' });
                },
              })
            )
          )
        )
      ),
      validateToken: rxMethod<{ accessToken: string }>(
        pipe(
          tap(() => patchState(store, { status: 'loading' })),
          switchMap(({ accessToken }) =>
            authService.validateToken(accessToken).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    status: 'idle',
                    jwt: response?.jwt,
                    refreshToken: response?.refreshToken,
                    user: response?.user,
                  });
                  storage.setItem(SIGNAL_JWT_KEY, response?.jwt, storeName);
                  storage.setItem(
                    SIGNAL_REFRESH_KEY,
                    response?.refreshToken,
                    storeName
                  );

                  router.navigate(NAVIGATE_TO_PRIVATE_AREA);
                },
                error: () => {
                  patchState(store, { status: 'error' });
                },
              })
            )
          )
        )
      ),
    })
  ),
  withHooks({
    onInit({ init }) {
      init();
    },
  })
);
